import { useState, useEffect, useCallback } from "react";
import { listRecipes } from "@/lib/atlasApi";
import { Button } from "@/components/ui/button";
import { FileText, RefreshCw, CheckCircle2, Clock, Beaker, Coins } from "lucide-react";

const STATUS = {
  SYNCED: { bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.3)", text: "#10B981", icon: CheckCircle2, label: "Sincronizada" },
  PENDING_SYNC: { bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.3)", text: "#F59E0B", icon: Clock, label: "Pendente" },
  LOCAL_SAVED: { bg: "rgba(0,229,255,0.12)", border: "rgba(0,229,255,0.3)", text: "#00E5FF", icon: CheckCircle2, label: "Gravada" },
};

export default function DraftsPage() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const d = await listRecipes(); setDrafts(d.drafts || []); }
    catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="h-full flex flex-col bg-[#0B0F17]" data-testid="drafts-page">
      <div className="px-6 h-14 flex items-center justify-between border-b border-[#2A364F] shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#E05A2B]" />
          <div>
            <h1 className="text-base font-semibold text-gray-100 leading-tight">Fichas Exportadas</h1>
            <p className="text-[11px] text-slate-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>rascunhos enviados ao SOP &amp; RAFT</p>
          </div>
        </div>
        <Button data-testid="drafts-refresh" variant="outline" size="sm" onClick={refresh} className="border-[#2A364F]">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto grid gap-3 sm:grid-cols-2">
          {drafts.length === 0 && (
            <p className="text-sm text-slate-500 col-span-full" data-testid="drafts-empty">
              Nenhuma ficha exportada ainda. Gere uma no Canvas ou no SousChef AI.
            </p>
          )}
          {drafts.map((d) => {
            const st = STATUS[d.status] || STATUS.PENDING_SYNC;
            const Icon = st.icon;
            const p = d.payload || {};
            return (
              <div key={d.draft_id} className="rounded-xl border border-[#2A364F] bg-[#161E2E] p-4" data-testid={`draft-${d.draft_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-100 truncate">{p.title || "Sem título"}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{p.authored_by || d.authored_by}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium shrink-0"
                        style={{ backgroundColor: st.bg, border: `1px solid ${st.border}`, color: st.text }}>
                    <Icon className="h-3 w-3" /> {st.label}
                  </span>
                </div>
                {p.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{p.description}</p>}
                <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
                  {p.origin && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#0B0F17] border border-[#2A364F] text-slate-400 uppercase tracking-wider">
                      {p.origin}
                    </span>
                  )}
                  {typeof p.pairing_metadata?.synergy_index === "number" && (
                    <span className="inline-flex items-center gap-1 text-[#E05A2B]" data-testid="draft-synergy">
                      Sinergia {Math.round(p.pairing_metadata.synergy_index * 100)}%
                    </span>
                  )}
                  {typeof p.cost_estimate?.total_cost === "number" && (
                    <span className="inline-flex items-center gap-1 text-[#FACC15]" data-testid="draft-cost">
                      <Coins className="h-3 w-3" /> ~{p.cost_estimate.currency} {p.cost_estimate.total_cost}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(p.ingredients || []).map((i, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#0B0F17] border border-[#2A364F] text-slate-300">
                      <Beaker className="h-2.5 w-2.5 text-[#00E5FF]" />{i.sop_raft_id}{i.quantity ? ` · ${i.quantity}${i.unit || ""}` : ""}
                    </span>
                  ))}
                </div>
                {d.status === "PENDING_SYNC" && d.sync_error && (
                  <p className="text-[10px] text-amber-500/80 mt-2 font-mono">sync: {d.sync_error}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
